import React, { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useI18n } from "../lib/i18n";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { QrCode, Pencil, Drop, ShieldCheck, HandHeart, Translate, ArrowRight, CheckCircle, PersonSimpleRun, Baby, FirstAidKit, UsersThree, MusicNotes, Mountains, Sun, PawPrint, LinkSimple } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/14ff286b-f186-46bf-8707-6e55684b5822/images/b70029c5f377828d4275ac78698225f1c1ab3c2992301eb73abbd6a2acb1745c.png";
const LIFESTYLE_IMG = "https://static.prod-images.emergentagent.com/jobs/14ff286b-f186-46bf-8707-6e55684b5822/images/5678c49a821965f928797ff69a9d81b1967fb47f99b69046cae5c7fe9afc6279.png";
const ELDERLY = "https://images.pexels.com/photos/12013184/pexels-photo-12013184.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const ACTIVE = "https://images.pexels.com/photos/17925633/pexels-photo-17925633.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function LandingPage() {
    const { t, lang } = useI18n();
    const [order, setOrder] = useState({ name: "", phone: "", email: "", address: "", quantity: 1, notes: "" });
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    const validate = () => {
        const e = {};
        if (!order.name.trim()) e.name = t.order.fieldRequired;
        if (!order.phone.trim()) e.phone = t.order.fieldRequired;
        if (!order.address.trim()) e.address = t.order.fieldRequired;
        if (order.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(order.email.trim())) e.email = t.order.emailInvalid;
        return e;
    };

    const submit = async (e) => {
        e.preventDefault();
        const v = validate();
        setErrors(v);
        if (Object.keys(v).length > 0) {
            const firstKey = Object.keys(v)[0];
            const el = document.querySelector(`[data-testid="order-${firstKey}"]`);
            if (el) { el.focus(); el.scrollIntoView({ behavior: "smooth", block: "center" }); }
            toast.error(v[firstKey]);
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                name: order.name.trim(),
                phone: order.phone.trim(),
                email: order.email.trim() || null,
                address: order.address.trim(),
                quantity: order.quantity,
                notes: order.notes.trim(),
                language: lang,
            };
            await api.post("/orders", payload);
            toast.success(t.order.success);
            setOrder({ name: "", phone: "", email: "", address: "", quantity: 1, notes: "" });
            setErrors({});
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-grain min-h-screen" data-testid="landing-page">
            <Navbar />

            {/* HERO */}
            <section className="relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 pt-16 md:pt-24 pb-20 md:pb-32 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7 animate-float-in">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-brand-border text-xs font-mono tracking-widest text-brand-text/70">
                            <span className="w-1.5 h-1.5 rounded-full bg-brand-primary animate-pulse" />
                            {t.hero.tag.toUpperCase()}
                        </div>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] mt-6 text-brand-text">
                            {t.hero.title1}
                            <br />
                            <span className="italic text-brand-primary">{t.hero.title2}</span>
                        </h1>
                        <p className="mt-6 text-lg text-brand-text/70 max-w-xl leading-relaxed">{t.hero.sub}</p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <a href="#order" data-testid="hero-cta-order" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full btn-primary font-semibold">
                                {t.hero.ctaPrimary}
                                <ArrowRight size={18} weight="bold" />
                            </a>
                            <a href="#how" data-testid="hero-cta-how" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-brand-text/20 hover:border-brand-text hover:bg-white transition font-semibold text-brand-text">
                                {t.hero.ctaSecondary}
                            </a>
                        </div>
                        <p className="mt-6 text-xs font-mono tracking-widest text-brand-textMuted uppercase">{t.hero.trust}</p>
                    </div>

                    <div className="lg:col-span-5 relative">
                        <div className="relative rounded-3xl overflow-hidden bg-white border border-brand-border shadow-[0_20px_60px_rgba(28,28,26,0.10)] hover-lift">
                            <img src={HERO_IMG} alt="QR Bracelet" className="w-full aspect-[4/5] object-cover" />
                        </div>
                        <div className="absolute -bottom-6 -left-6 hidden md:block bg-white rounded-2xl border border-brand-border p-4 shadow-lg w-56">
                            <div className="text-xs font-mono tracking-widest text-brand-textMuted">{t.hero.scanBadge}</div>
                            <div className="font-display text-2xl mt-1">{t.hero.scanResult}</div>
                            <div className="text-xs text-brand-textMuted mt-1">{t.hero.scanSpeed}</div>
                        </div>
                        <div className="absolute -top-6 -right-6 hidden md:flex items-center gap-2 bg-brand-secondary text-white rounded-full px-4 py-2 text-xs font-mono tracking-widest">
                            <CheckCircle size={14} weight="fill" /> NO APP NEEDED
                        </div>
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section id="how" className="py-20 md:py-32 bg-white border-y border-brand-border">
                <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
                    <div className="max-w-2xl">
                        <div className="font-mono tracking-widest text-xs text-brand-primary">/ HOW IT WORKS</div>
                        <h2 className="font-display text-4xl md:text-5xl mt-3">{t.how.title}</h2>
                        <p className="mt-3 text-brand-text/70">{t.how.sub}</p>
                    </div>
                    <div className="mt-12 grid md:grid-cols-3 gap-6">
                        {[
                            { n: "01", title: t.how.s1Title, desc: t.how.s1Desc, icon: HandHeart },
                            { n: "02", title: t.how.s2Title, desc: t.how.s2Desc, icon: Pencil },
                            { n: "03", title: t.how.s3Title, desc: t.how.s3Desc, icon: ShieldCheck },
                        ].map((s) => (
                            <div key={s.n} className="rounded-3xl border border-brand-border p-7 hover-lift bg-brand-bg" data-testid={`how-step-${s.n}`}>
                                <div className="flex items-center justify-between">
                                    <span className="font-mono text-xs tracking-widest text-brand-textMuted">{s.n}</span>
                                    <span className="w-10 h-10 rounded-2xl bg-brand-secondary text-white flex items-center justify-center">
                                        <s.icon size={20} weight="bold" />
                                    </span>
                                </div>
                                <h3 className="font-display text-2xl mt-6">{s.title}</h3>
                                <p className="text-sm text-brand-text/70 mt-2 leading-relaxed">{s.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FEATURES BENTO */}
            <section id="features" className="py-20 md:py-32">
                <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
                    <div className="max-w-2xl">
                        <div className="font-mono tracking-widest text-xs text-brand-primary">/ FEATURES</div>
                        <h2 className="font-display text-4xl md:text-5xl mt-3">{t.features.title}</h2>
                        <p className="mt-3 text-brand-text/70">{t.features.sub}</p>
                    </div>
                    <div className="mt-12 grid md:grid-cols-6 gap-6">
                        <div className="md:col-span-3 row-span-2 rounded-3xl bg-brand-secondary text-white p-8 overflow-hidden relative" data-testid="feat-edit">
                            <Pencil size={36} weight="duotone" />
                            <h3 className="font-display text-3xl mt-6">{t.features.f1Title}</h3>
                            <p className="text-white/70 mt-2">{t.features.f1Desc}</p>
                            <img src={LIFESTYLE_IMG} alt="" className="absolute -right-10 -bottom-10 w-64 opacity-30 rounded-3xl" />
                        </div>
                        <div className="md:col-span-3 rounded-3xl border border-brand-border bg-white p-7 hover-lift" data-testid="feat-qr">
                            <QrCode size={28} weight="duotone" className="text-brand-primary" />
                            <h3 className="font-display text-2xl mt-5">{t.features.f2Title}</h3>
                            <p className="text-sm text-brand-text/70 mt-2">{t.features.f2Desc}</p>
                        </div>
                        <div className="md:col-span-3 rounded-3xl border border-brand-border bg-white p-7 hover-lift" data-testid="feat-offline">
                            <ShieldCheck size={28} weight="duotone" className="text-brand-primary" />
                            <h3 className="font-display text-2xl mt-5">{t.features.f3Title}</h3>
                            <p className="text-sm text-brand-text/70 mt-2">{t.features.f3Desc}</p>
                        </div>
                        <div className="md:col-span-2 rounded-3xl border border-brand-border bg-white p-7 hover-lift">
                            <Drop size={28} weight="duotone" className="text-brand-primary" />
                            <h3 className="font-display text-xl mt-5">{t.features.f4Title}</h3>
                            <p className="text-sm text-brand-text/70 mt-2">{t.features.f4Desc}</p>
                        </div>
                        <div className="md:col-span-2 rounded-3xl border border-brand-border bg-white p-7 hover-lift">
                            <HandHeart size={28} weight="duotone" className="text-brand-primary" />
                            <h3 className="font-display text-xl mt-5">{t.features.f5Title}</h3>
                            <p className="text-sm text-brand-text/70 mt-2">{t.features.f5Desc}</p>
                        </div>
                        <div className="md:col-span-2 rounded-3xl border border-brand-border bg-white p-7 hover-lift">
                            <Translate size={28} weight="duotone" className="text-brand-primary" />
                            <h3 className="font-display text-xl mt-5">{t.features.f6Title}</h3>
                            <p className="text-sm text-brand-text/70 mt-2">{t.features.f6Desc}</p>
                        </div>
                        <div className="md:col-span-6 rounded-3xl bg-brand-primary text-white p-8 overflow-hidden relative" data-testid="feat-event">
                            <div className="flex items-start justify-between gap-6 flex-wrap">
                                <div className="max-w-xl">
                                    <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/15 text-[10px] font-mono tracking-widest">
                                        <LinkSimple size={12} weight="bold" /> NEW
                                    </div>
                                    <h3 className="font-display text-3xl md:text-4xl mt-4">{t.features.f7Title}</h3>
                                    <p className="text-white/85 mt-2 leading-relaxed">{t.features.f7Desc}</p>
                                </div>
                                <div className="flex items-center gap-3 self-center">
                                    <MusicNotes size={56} weight="duotone" />
                                    <Mountains size={56} weight="duotone" />
                                    <Sun size={56} weight="duotone" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* USE CASES */}
            <section id="use-cases" className="py-20 md:py-32 bg-white border-y border-brand-border">
                <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
                    <div className="max-w-2xl">
                        <div className="font-mono tracking-widest text-xs text-brand-primary">/ USE CASES</div>
                        <h2 className="font-display text-4xl md:text-5xl mt-3">{t.useCases.title}</h2>
                        <p className="mt-3 text-brand-text/70">{t.useCases.sub}</p>
                    </div>
                    <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            { title: t.useCases.uc1, desc: t.useCases.uc1Desc, icon: Baby, img: null, accent: false },
                            { title: t.useCases.uc2, desc: t.useCases.uc2Desc, icon: UsersThree, img: ELDERLY, accent: false },
                            { title: t.useCases.uc3, desc: t.useCases.uc3Desc, icon: PersonSimpleRun, img: ACTIVE, accent: false },
                            { title: t.useCases.uc4, desc: t.useCases.uc4Desc, icon: FirstAidKit, img: null, accent: false },
                            { title: t.useCases.uc5, desc: t.useCases.uc5Desc, icon: Sun, img: null, accent: false },
                            { title: t.useCases.uc6, desc: t.useCases.uc6Desc, icon: Mountains, img: null, accent: false },
                            { title: t.useCases.uc7, desc: t.useCases.uc7Desc, icon: MusicNotes, img: null, accent: true },
                            { title: t.useCases.uc8, desc: t.useCases.uc8Desc, icon: PawPrint, img: null, accent: false },
                        ].map((c, i) => (
                            <div key={i} className="rounded-3xl overflow-hidden border border-brand-border bg-brand-bg hover-lift" data-testid={`usecase-${i}`}>
                                {c.img ? (
                                    <div className="aspect-[4/3] overflow-hidden">
                                        <img src={c.img} alt="" className="w-full h-full object-cover" />
                                    </div>
                                ) : (
                                    <div className={`aspect-[4/3] flex items-center justify-center ${c.accent ? "bg-brand-primary" : "bg-brand-secondary"} text-white`}>
                                        <c.icon size={64} weight="duotone" />
                                    </div>
                                )}
                                <div className="p-6">
                                    <h3 className="font-display text-2xl">{c.title}</h3>
                                    <p className="text-sm text-brand-text/70 mt-2">{c.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ORDER FORM */}
            <section id="order" className="py-20 md:py-32">
                <div className="max-w-6xl mx-auto px-4 md:px-8 lg:px-12 grid lg:grid-cols-12 gap-10 items-start">
                    <div className="lg:col-span-5">
                        <div className="font-mono tracking-widest text-xs text-brand-primary">/ ORDER</div>
                        <h2 className="font-display text-4xl md:text-5xl mt-3">{t.order.title}</h2>
                        <p className="mt-3 text-brand-text/70">{t.order.sub}</p>
                        <div className="mt-8 rounded-3xl bg-brand-secondary text-white p-8">
                            <div className="font-mono text-xs tracking-widest text-white/60">QR BRACELET PRO</div>
                            <div className="font-display text-5xl mt-2">{t.order.price}</div>
                            <div className="mt-2 text-xs font-mono tracking-widest text-white/60 uppercase">{t.order.priceNote}</div>
                            <ul className="mt-6 space-y-2 text-sm text-white/80">
                                <li className="flex gap-2"><CheckCircle size={18} weight="fill" /> {t.order.b1}</li>
                                <li className="flex gap-2"><CheckCircle size={18} weight="fill" /> {t.order.b2}</li>
                                <li className="flex gap-2"><CheckCircle size={18} weight="fill" /> {t.order.b3}</li>
                                <li className="flex gap-2"><CheckCircle size={18} weight="fill" /> {t.order.b4}</li>
                            </ul>
                        </div>
                    </div>
                    <form onSubmit={submit} noValidate className="lg:col-span-7 rounded-3xl border border-brand-border bg-white p-8 space-y-4" data-testid="order-form">
                        <div className="grid md:grid-cols-2 gap-4">
                            <Field label={t.order.name} required value={order.name} onChange={(v) => { setOrder({ ...order, name: v }); if (errors.name) setErrors({ ...errors, name: null }); }} testid="order-name" error={errors.name} />
                            <Field label={t.order.phone} required value={order.phone} onChange={(v) => { setOrder({ ...order, phone: v }); if (errors.phone) setErrors({ ...errors, phone: null }); }} testid="order-phone" error={errors.phone} />
                        </div>
                        <Field label={t.order.email} type="email" value={order.email} onChange={(v) => { setOrder({ ...order, email: v }); if (errors.email) setErrors({ ...errors, email: null }); }} testid="order-email" error={errors.email} />
                        <Field label={t.order.address} required value={order.address} onChange={(v) => { setOrder({ ...order, address: v }); if (errors.address) setErrors({ ...errors, address: null }); }} testid="order-address" error={errors.address} />
                        <div className="grid md:grid-cols-3 gap-4">
                            <Field label={t.order.qty} type="number" min={1} max={50} value={order.quantity} onChange={(v) => setOrder({ ...order, quantity: Math.max(1, parseInt(v || "1", 10)) })} testid="order-qty" />
                        </div>
                        <Field label={t.order.notes} textarea value={order.notes} onChange={(v) => setOrder({ ...order, notes: v })} testid="order-notes" />
                        <button type="submit" disabled={submitting} className="w-full py-4 rounded-full btn-primary font-semibold disabled:opacity-60" data-testid="order-submit">
                            {submitting ? "..." : t.order.cta}
                        </button>
                    </form>
                </div>
            </section>

            <Footer />
        </div>
    );
}

function Field({ label, value, onChange, type = "text", textarea, required, min, max, testid, error }) {
    const borderClass = error ? "border-brand-critical focus:border-brand-critical" : "border-brand-border focus:border-brand-text";
    return (
        <label className="block">
            <span className="text-xs font-mono tracking-widest text-brand-textMuted uppercase">{label}{required && <span className="text-brand-primary"> *</span>}</span>
            {textarea ? (
                <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} className={`mt-2 w-full rounded-xl border bg-brand-bg px-4 py-3 text-sm focus:outline-none transition ${borderClass}`} data-testid={testid} aria-invalid={!!error} />
            ) : (
                <input
                    type={type}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    min={min}
                    max={max}
                    className={`mt-2 w-full rounded-xl border bg-brand-bg px-4 py-3 text-sm focus:outline-none transition ${borderClass}`}
                    data-testid={testid}
                    aria-invalid={!!error}
                />
            )}
            {error && <span className="block mt-1.5 text-xs text-brand-critical font-medium" data-testid={`${testid}-error`}>{error}</span>}
        </label>
    );
}
